import moment from 'moment';

import Service, {
  City,
  Experiment,
  FormatUnixTime,
  GetInitials,
  Vehicle,
} from '../lib/App';
import { Constraint } from '../lib/PlannerDTOs';

const COMPLETED = 'completed';
const PENDING = 'pending';
const REQUESTED = 'requested';
const ABORTED = 'aborted';
const STARTED = 'started';

export class ExperimentModel {
  experiment: Experiment;

  constructor(experiment: Experiment) {
    this.experiment = experiment;
  }

  /**
   * Gets Completed const.
   */
  public static get COMPLETED() {
    return COMPLETED;
  }

  /**
   * Gets Pending const.
   */
  public static get PENDING() {
    return PENDING;
  }

  /**
   * Gets Requested const.
   */
  public static get REQUESTED() {
    return REQUESTED;
  }

  /**
   * Gets Aborted const.
   */
  public static get ABORTED() {
    return ABORTED;
  }

  /**
   * Gets Started const.
   */
  public static get STARTED() {
    return STARTED;
  }

  /**
   * Gets the description of the experiment.
   */
  public description(): string {
    if (this.experiment.metadata === undefined) {
      return '';
    }
    return this.experiment.metadata.description ?? '';
  }

  /**
   * Gets the abortReason of the experiment.
   */
  public abortReason(): string {
    if (this.experiment.metadata === undefined) {
      return '';
    }
    return this.experiment.metadata.abortReason ?? '';
  }

  /**
   * Gets the original name of experiment
   */
  public name(): string {
    return this.experiment.name ?? '';
  }

  /**
   * Gets the array of constraints
   */
  public constraints() {
    return this.experiment.constraints ?? [];
  }

  /**
   * Gets the original name of experiment
   */
  public isAutoCancelled(): boolean {
    if (
      this.experiment.metadata &&
      this.experiment.metadata.abortReason !== undefined
    ) {
      return this.experiment.metadata.abortReason.startsWith('autoCancel');
    }
    return false;
  }

  /**
   * Gets the created date of the experiment.
   */
  public createdAt(): moment.Moment {
    if (this.experiment.createdAt) {
      return moment(this.experiment.createdAt);
    }
    return moment.unix(0);
  }

  /**
   * Gets the start date of the experiment.
   */
  public startDate(): moment.Moment {
    const startTimeEpoch = this.experiment?.constraints?.filter(
      (constraint) => constraint?.op === '>',
    );

    if (startTimeEpoch?.length) {
      return moment.unix(parseInt(startTimeEpoch[0].value, 10));
    }

    if (this.experiment.startTime) {
      return moment.unix(this.experiment.startTime);
    }
    return moment.unix(0);
  }

  /**
   * Gets the end date of the experiment.
   */
  public endDate(): moment.Moment {
    const endTime = this.experiment?.constraints?.filter(
      (constraint) => constraint?.op === '<',
    );

    if (endTime?.length) {
      return moment.unix(parseInt(endTime[0].value, 10));
    }

    if (this.experiment.endTime) {
      return moment.unix(this.experiment.endTime);
    }
    return moment.unix(0);
  }

  /**
   * Gets the state of the experiment.
   */
  public state(): string {
    if (this.experiment.state === undefined) {
      return '';
    }
    return this.experiment.state.toLowerCase();
  }

  /**
   * Check if the experiment is completed
   */
  public isCompleted(): boolean {
    return this.state() === 'completed';
  }

  /**
   * Gets how many days left for the experiment.
   */
  public left(): string {
    if (this.endDate().isBefore(moment(new Date()))) {
      return 'finished';
    }

    return moment(this.endDate()).toNow(true);
  }

  /**
   * Gets the duration of the experiment
   */
  public duration(): moment.Duration {
    return moment.duration(
      this.startDate().unix() - this.endDate().unix(),
      'seconds',
    );
  }

  public hasNoEndDate(): boolean {
    return this.endDate().isSame(moment.unix(0));
  }

  /**
   * Gets the owner initials for the experiment.
   */
  public ownerInitials(): string {
    const email: string = this.experiment.owner ?? '';
    return GetInitials(email);
  }

  /**
   * Gets the owner email for the experiment.
   */
  public owner(): string {
    return this.experiment.owner ?? '';
  }

  /**
   * Gets the progres of the experiment.
   */
  public progress(): number {
    const start = this.startDate().unix();
    const end = this.endDate().unix();
    const now = FormatUnixTime(new Date());
    if (now >= end) {
      return 100;
    } else if (now <= start) {
      return 0;
    } else {
      return Math.round(((now - start) / (end - start)) * 100);
    }
  }

  /**
   * Gets a slot by vehicle ID.
   * @param id the vehicle id.
   */
  public getSlotByVehicle(id: number): string {
    const vehicle: Vehicle = Service.cache!.getVehicle(id)!;
    const city: City = Service.cache!.getCity(vehicle.cityID)!;
    return vehicle.name + ', ' + city.name;
  }

  /**
   * Gets the list of countries on which the experiment is running.
   */
  public countries(): ExperimentCountry[] {
    const result: ExperimentCountry[] = [];

    // Get the iso code for the country and add to the set
    for (const vehicle of this.vehicles()) {
      const city = Service.cache!.getCity(vehicle.cityID);
      let country: ExperimentCountry | undefined = result.find(
        (v) => v.name === city!.countryCode,
      );
      if (!country) {
        country = new ExperimentCountry();
        country.name = city!.countryCode;
        country.vehicles = [];
        result.push(country);
      }

      // Add a new vehicle as well
      country.vehicles.push(vehicle.name + ' in ' + city!.name);
    }

    // Add ctry flag for ctry level experiments
    if (result.length === 0) {
      const c: Constraint[] = this.experiment.constraints.filter(
        ({ op, target }) => op === '=' && target === 'ctry',
      );
      if (c.length !== 0) {
        const country: ExperimentCountry = new ExperimentCountry();
        country.name = c[0].value!;
        country.vehicles = [];
        result.push(country);
      }
    }

    // Return unique countries
    return result.filter((v, i, a) => a.indexOf(v) === i);
  }

  /**
   * Gets the list of all cities in the experiment.
   */
  public cities(): City[] {
    const output: City[] = [];

    // Get the city from the constraint
    const c: Constraint[] = this.experiment.constraints.filter(
      ({ op, target }) => op === '=' && target === 'city',
    );
    if (c.length > 0) {
      const city: City | undefined = Service.cache!.getCity(
        parseInt(c[0].value!, 10),
      );
      if (city) {
        output.push(city);
      }
    }

    return output;
  }

  /**
   * Gets the list of all vehicles in the experiment.
   */
  public vehicles(): Vehicle[] {
    const output: Vehicle[] = [];
    const cities: Map<number, boolean> = new Map<number, boolean>();

    // Push all assignment vehicles
    if (this.experiment.slotting.byAssignment) {
      for (const id of this.experiment.slotting.byAssignment) {
        const vehicle: Vehicle | undefined = Service.cache!.getVehicle(id);
        if (vehicle) {
          cities.set(vehicle.cityID, true);
          output.push(vehicle);
        }
      }
    }

    // Get the vehicles from the constraint
    const c: Constraint[] = this.experiment.constraints.filter(
      ({ op, target }) => op === 'in' && target === 'svc',
    );
    if (c.length > 0 && c[0].value!.indexOf('[') >= 0) {
      for (const v of JSON.parse(c[0].value!)) {
        const vehicle: Vehicle | undefined = Service.cache!.getVehicle(
          parseInt(v, 10),
        );
        if (vehicle) {
          cities.set(vehicle.cityID, true);
          output.push(vehicle);
        }
      }
    }

    // Fill in the vehicles for cities
    for (const city of this.cities()) {
      if (!cities.get(city.id)) {
        output.push({
          id: -city.id,
          cityID: city.id,
          name: 'All Services',
          serviceType: 'ALL',
          icon: '',
        });
      }
    }

    return output;
  }
  /**
   * Returns if an experiment is an overlapping experiment by checking approval flow length
   */
  public isOverlap(): boolean {
    const approval = this.experiment?.approvalFlow?.data;
    if (!approval) {
      return false;
    }
    return approval.length > 1;
  }
  /**
   * Returns if an experiment is using xscheduler by checking scheduling definition conditions length
   */
  public isUsingXScheduler(): boolean {
    const schedulingDef = this.experiment?.schedulingDefinition?.conditions;
    if (!schedulingDef) {
      return false;
    }
    return schedulingDef.length > 0;
  }

  public isAutoAdaptive(): boolean {
    return this.experiment.autoAdaptive.name !== undefined;
  }
}

export class ExperimentCountry {
  public name: string;
  public vehicles: string[];
}
